import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, from, of } from 'rxjs';
import { LoadingMask } from 'src/app/classes/loading-mask';
import { Site } from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';

import { AddEditSiteModalComponent } from '../add-edit-site-modal/add-edit-site-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-sites-upload-modal',
  templateUrl: './sites-upload-modal.component.html',
  styleUrl: './sites-upload-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesUploadModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private ModalService: NgbModal,
    private ToastService: ToastService,
    private SiteService: SiteService,
    private HostElement: ElementRef
  ) {}

  @Input() Sites?: Site[];

  title: string = 'Sites Upload';
  missingRequiredDataCount: number = 0;
  matchingExistingSiteCount: number = 0;
  bothMissingRequiredFieldsAndMatchingExistingCount: number = 0;

  rowData: Observable<any[]> = of([]);
  rowClassRules = {
    'gridRow-red': (params: any) =>
      params.data.missingRequiredData === true &&
      !params.data.matchingExistingSite,
    'gridRow-yellow': (params: any) =>
      params.data.matchingExistingSite === true &&
      !params.data.missingRequiredData,
    'gridRow-orange': (params: any) =>
      params.data.missingRequiredData === true &&
      params.data.matchingExistingSite === true
  };
  reloadGrid: boolean = false;
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['view', 'delete']
  };

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  upload() {
    if (this.getSumOfCounts() > 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload sites. There are errors in the uploaded file.',
        { returnError: true }
      );
    }

    if (!this.Sites || this.Sites.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload sites. There are no sites to upload.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Uploading Sites...'
    });

    this.SiteService.saveSites(this.Sites).subscribe({
      next: () => {
        mask.destroy();
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully uploaded sites file.'
        );
        this.activeModal.close();
      },
      error: () => {
        mask.destroy();
      }
    });
  }

  actionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: Site;
  }) {
    if (!this.Sites) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot complete action. Not sites data found.'
      );
    }

    if (action === 'view') {
      const modalRef = this.ModalService.open(AddEditSiteModalComponent, {
        centered: true,
        size: 'xl'
      });
      Object.assign(modalRef.componentInstance, { Site: rowData });

      modalRef.closed.subscribe({
        next: () => {
          Object.assign(this.Sites!, rowData);
          this.reloadGrid = true;
          this.calculateCounts(this.Sites!);
        }
      });
    }

    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md' }
      );
      Object.assign(componentInstance, {
        displayText:
          'Are you sure you want to remove this site from the upload?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            const index = this.Sites?.findIndex(
              (site) => site.siteKey === rowData.siteKey
            );

            if (index !== -1) {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully removed site from upload.'
              );
              this.Sites?.splice(index!, 1);
              this.rowData = of([...this.Sites!]);
              this.reloadGrid = true;
            }
          }
        }
      });
    }
  }

  getSumOfCounts() {
    return (
      this.missingRequiredDataCount +
      this.matchingExistingSiteCount +
      this.bothMissingRequiredFieldsAndMatchingExistingCount
    );
  }

  calculateCounts(sites: Site[]) {
    if (!sites) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot calculate counts. No Sites data found.',
        { returnError: true }
      );
    }

    this.missingRequiredDataCount = sites.filter(
      (site: any) =>
        site.missingRequiredData === true && !site.matchingExistingSite
    ).length;
    this.matchingExistingSiteCount = sites.filter(
      (site: any) =>
        site.matchingExistingSite === true && !site.missingRequiredData
    ).length;
    this.bothMissingRequiredFieldsAndMatchingExistingCount = sites.filter(
      (site: any) =>
        site.matchingExistingSite === true && site.missingRequiredData === true
    ).length;
  }

  ngOnInit(): void {
    if (!this.Sites || this.Sites.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload Sites. No sites found.',
        { returnError: true }
      );
    }

    this.Sites.forEach((site) => {
      const { StreetAddress } = site;

      if (StreetAddress) {
        Object.assign(StreetAddress, { isStreet: true });
      }
    });

    this.rowData = of([...this.Sites]);

    this.calculateCounts(this.Sites);
  }
}
